/**
 * The Quality tab's overview (design board 4), action-led: the score ring with the status chips (they filter the cards)
 * and the last full run; "What to do next", the ordered steps of the latest run with one button each (or "All clear.
 * Publish is open."); the release gate (collapsed to one line when open, expanded with what blocks first when blocked,
 * every rule with a short description, and the production-readiness items); the service filter; the three groups of
 * cards (Does it work? Is it safe? Can I prove it?) in a grid that never leaves half-empty rows; "Can I prove it?" also
 * holds the Build provenance and Compliance mapping cards (QualityProveCards, not checks). Each section has a stable id
 * (`SECTIONS`) the header's section menu scrolls to.
 *
 * A card: its status (a spinner while it runs), the big number with its context, three lines, the trend (or "First
 * run" before there is one), a "What to do" line with its primary action when it needs attention or tells something
 * (Fix with AI, Create lockfiles, Write tests with AI, Check the pages, Open), "Blocks Publish", Run and Open.
 *
 * Create lockfiles reports where it was clicked (under the step, or in the card): its progress, then its result. So
 * does Run (`runNote`: why the checks did not start, at the card, step or banner it was clicked on). A run the sandbox
 * restart stopped (`overview.interrupted`) shows as a banner with Retry, and its cards say "Interrupted — the sandbox
 * restarted" with Retry instead of "Not run yet".
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowRight, CaretDown, CaretUp, CheckCircle, DownloadSimple, LockSimple, LockSimpleOpen, Play, Sparkle,
} from '@phosphor-icons/react';
import { Banner, CheckIcon, Pill, RunNote, ScoreRing, Sparkline, StatusPill, scrollInPanel } from './QualityCommon';
import {
  CHECKS, FIXABLE, GROUPS, CHECK_PURPOSE, cardAction, cardLines, cardPrompt, cardTone, checkTitle, findingLine, gapsOf, gateRulePrompt, interruptedCheck, plural, ruleState, waivedOf,
  readinessItemOf, readinessPrompt, relativeTime, shownStatus, writeTestsPrompt, scoreTitle, sparkPoints, statusCounts, statusOf, trendLabel, whenText,
} from './qualityModel';
import { matchesFilter, nextSteps, whatToDo } from './qualityNext';
import { AssessmentsCard, ComplianceCard, ProvenanceCard } from './QualityProveCards';
import { LockfileStatus } from './QualityLockfiles';

const TONE_WORDS = { fail: 'failing', warn: 'needing attention', pass: 'passing', info: 'info only', prog: 'in progress' };

/** The button of an action kind (`qualityNext.js`), wired to the overview's handlers. */
function ActionButton({ step, handlers, primary = true, size = 'sm', testId }) {
  const { kind, label } = step.action || {};
  if (!kind) { return null; }
  const run = handlers[kind];
  if (!run) { return null; }
  const busy = Boolean(handlers.busy?.(kind));
  const icon = kind === 'fix' || kind === 'writeTests' ? <Sparkle aria-hidden size={13} /> : kind === 'run' || kind === 'runAll' || kind === 'runA11y' ? <Play aria-hidden size={12} weight="fill" /> : <ArrowRight aria-hidden size={13} />;
  // Create lockfiles may take minutes (services start first): the button says it works.
  const working = busy && kind === 'lockfiles';
  return (
    <button aria-busy={working || undefined} className={`quality-btn is-${size}${primary ? ' is-primary' : ''}`} data-testid={testId} disabled={busy} onClick={() => run(step)} type="button">
      {working ? <><span aria-hidden className="quality-spinner is-xs" />Creating lockfiles…</> : <>{icon}{label}</>}
    </button>
  );
}

ActionButton.propTypes = { handlers: PropTypes.object.isRequired, primary: PropTypes.bool, size: PropTypes.string, step: PropTypes.object.isRequired, testId: PropTypes.string };

function NextSteps({ overview, handlers, canEdit, onPublish, lockfile = null }) {
  const steps = nextSteps(overview);
  const [all, setAll] = useState(false);
  const shown = all ? steps : steps.slice(0, 4);
  if (!steps.length) {
    return (
      <section aria-labelledby="quality-next-title" className="quality-card quality-next is-clear" data-testid="quality-next">
        <span aria-hidden className="quality-next-ok"><CheckCircle size={22} weight="fill" /></span>
        <div className="quality-grow">
          <h2 id="quality-next-title">All clear. Publish is open.</h2>
          <p className="quality-muted quality-small">Nothing to fix from the last run.</p>
        </div>
        {onPublish && <button className="quality-btn is-primary" data-testid="quality-next-publish" onClick={onPublish} type="button">Publish</button>}
      </section>
    );
  }
  return (
    <section aria-labelledby="quality-next-title" className="quality-card quality-next" data-testid="quality-next">
      <h2 id="quality-next-title">What to do next</h2>
      <ol className="quality-next-list">
        {shown.map((step) => (
          <li className={`is-${step.tone}`} data-testid={`quality-step-${step.key}`} key={step.key}>
            <span aria-hidden className={`quality-next-dot is-${step.tone}`} />
            <div className="quality-grow">
              <strong>{step.title}</strong>
              {step.blocks && <Pill className="is-xs" tone="fail">Blocks Publish</Pill>}
              {step.detail && <span className="quality-muted quality-small quality-next-detail">{step.detail}</span>}
              {lockfile?.at === `overview:step:${step.key}` && handlers.lockfileStatus(lockfile)}
              {handlers.runNoteAt(`overview:step:${step.key}`)}
            </div>
            {(canEdit || step.action?.kind === 'open') && <ActionButton handlers={handlers} step={{ ...step, origin: `overview:step:${step.key}` }} testId={`quality-step-action-${step.key}`} />}
          </li>
        ))}
      </ol>
      {/* The step it was clicked on is gone (the lockfiles were made): the result stays here. */}
      {lockfile?.at?.startsWith('overview:step:') && !shown.some((step) => lockfile.at === `overview:step:${step.key}`) && handlers.lockfileStatus(lockfile)}
      {handlers.runNoteOrphan(shown.map((step) => `overview:step:${step.key}`))}
      {steps.length > 4 && <button className="quality-link" onClick={() => setAll(!all)} type="button">{all ? 'Show fewer' : `Show ${steps.length - 4} more`}</button>}
    </section>
  );
}

NextSteps.propTypes = { canEdit: PropTypes.bool, handlers: PropTypes.object.isRequired, lockfile: PropTypes.object, onPublish: PropTypes.func, overview: PropTypes.object.isRequired };

function ReadinessItems({ rule, canEdit, onAskAI }) {
  const items = rule?.items || [];
  if (!items.length) { return null; }
  return (
    <ul aria-label="Production readiness" className="quality-gate-items" data-testid="quality-gate-readiness">
      {items.map((item) => (
        <li key={item.id}>
          <Pill className="is-xs" tone={item.blocks ? 'fail' : 'warn'}>{item.blocks ? 'Blocks' : 'Warns'}</Pill>
          <div className="quality-grow">
            <strong>{item.title}</strong>
            {(item.file || item.service) && <span className="quality-mono quality-small quality-muted"> · {item.file ? `${item.file}${item.line ? `:${item.line}` : ''}` : `services/${item.service}`}</span>}
            {item.why && <span className="quality-small quality-gate-why">{item.why}</span>}
            {item.fix && <span className="quality-small quality-muted">How to fix: {item.fix}</span>}
          </div>
          {canEdit && onAskAI && <button className="quality-btn is-sm" onClick={() => onAskAI(readinessPrompt([item]))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>}
        </li>
      ))}
    </ul>
  );
}

ReadinessItems.propTypes = { canEdit: PropTypes.bool, onAskAI: PropTypes.func, rule: PropTypes.object };

/**
 * The missing checks of the gate (no tests, tests that can't run, no coverage report, no linter, no lockfile): shown
 * even with the rules collapsed, since they never count as passing. The waived ones, with their reason, below.
 */
function MissingChecks({ gate, onOpen, onOpenSettings, canEdit }) {
  const gaps = gapsOf(gate);
  const waived = waivedOf(gate);
  if (!gaps.length && !waived.length) { return null; }
  return (
    <div className="quality-gate-missing" data-testid="quality-gate-missing">
      {gaps.length > 0 && (
        <>
          <p className="quality-small"><strong>Missing checks</strong> <span className="quality-muted">never count as passing{gaps.some((g) => g.blocking) ? ': add them, or waive them in Settings with a reason' : ''}.</span></p>
          <ul aria-label="Missing checks" className="quality-gate-items">
            {gaps.map((gap) => (
              <li data-testid={`quality-gap-${gap.id}`} key={gap.id}>
                <Pill className="is-xs" tone={gap.blocking ? 'fail' : 'warn'}>{gap.blocking ? 'Blocks Publish' : 'Missing'}</Pill>
                <div className="quality-grow">
                  <strong>{gap.label}{gap.service ? ` · ${gap.service}` : ''}</strong>
                  <span className="quality-small quality-muted">{gap.detail}</span>
                  {gap.fix && <span className="quality-small quality-gate-why">{gap.fix}</span>}
                </div>
                <button className="quality-btn is-sm" onClick={() => onOpen(gap.check)} type="button">Open</button>
              </li>
            ))}
          </ul>
        </>
      )}
      {waived.length > 0 && (
        <p className="quality-small quality-muted" data-testid="quality-gate-waived">
          Waived on purpose: {waived.map((w) => `${w.label}${w.service ? ` (${w.service})` : ''}: ${w.waived?.reason}`).join(' · ')}
          {' '}<button className="quality-link" onClick={onOpenSettings} type="button">{canEdit ? 'Review' : 'See'}</button>
        </p>
      )}
    </div>
  );
}

MissingChecks.propTypes = { canEdit: PropTypes.bool, gate: PropTypes.object, onOpen: PropTypes.func.isRequired, onOpenSettings: PropTypes.func.isRequired };

function GateCard({ gate, checks, canEdit, onAskAI, onOpen, onOpenSettings, expandSignal = 0 }) {
  const on = (gate?.rules || []).filter((r) => r.on);
  const failing = on.filter((r) => r.status === 'fail');
  const [open, setOpen] = useState(failing.length > 0);
  const ref = useRef(null);
  useEffect(() => { if (failing.length) { setOpen(true); } }, [failing.length]);
  useEffect(() => {
    if (!expandSignal) { return; }
    setOpen(true);
    scrollInPanel(ref.current);
  }, [expandSignal]);
  if (!gate) { return null; }
  const passing = on.filter((r) => r.status === 'pass');
  const readiness = (gate.rules || []).find((r) => r.id === 'readiness');
  const blocked = Boolean(gate.blocked);
  let title;
  let sub;
  if (gate.neverRan) {
    title = 'Release gate: not checked yet';
    sub = 'Run the checks to see what would block Publish.';
  } else if (blocked) {
    title = 'Release gate: Publish is blocked';
    const missing = failing.filter((r) => r.missing).length;
    sub = `${plural(failing.length, 'rule')} of ${on.length} ${failing.length === 1 ? 'fails' : 'fail'}${missing ? ` · ${missing} because a check is missing` : ''}`;
  } else if (gate.override && failing.length) {
    title = 'Release gate: open with an override';
    sub = `${gate.override.by?.name || gate.override.by?.email || 'An owner'}: ${gate.override.reason}`;
  } else {
    title = 'Release gate: Publish is open';
    const unknown = on.filter((r) => r.status === 'unknown').length;
    sub = `${passing.length} of ${on.length} rules pass${unknown ? ` · ${unknown} not checked yet` : ''}`;
  }
  // Blocking first, then the others in their order: passing, not checked, off.
  const order = { fail: 0, pass: 1, unknown: 2, off: 3 };
  const rules = [...(gate.rules || [])].sort((a, b) => order[a.status] - order[b.status]);
  return (
    <section aria-labelledby="quality-gate-title" className={`quality-card quality-gate${blocked ? ' is-blocked' : ''}`} data-testid="quality-gate" id="quality-section-gate" ref={ref} tabIndex={-1}>
      <div className="quality-gate-head">
        <span aria-hidden className={`quality-gate-icon${blocked ? ' is-fail' : ' is-pass'}`}>{blocked ? <LockSimple size={16} /> : <LockSimpleOpen size={16} />}</span>
        <div className="quality-grow">
          <h2 id="quality-gate-title">{title}</h2>
          <span className="quality-muted" data-testid="quality-gate-sub">{sub}</span>
        </div>
        <button aria-controls="quality-gate-rules" aria-expanded={open} className="quality-btn is-sm is-quiet" data-testid="quality-gate-toggle" onClick={() => setOpen(!open)} type="button">
          {open ? <CaretUp aria-hidden size={13} /> : <CaretDown aria-hidden size={13} />}{open ? 'Hide rules' : 'Show rules'}
        </button>
        <button className="quality-link" onClick={onOpenSettings} type="button">{canEdit ? 'Edit rules' : 'See rules'}</button>
      </div>
      {gate.stale && !gate.neverRan && <p className="quality-muted quality-small">Files changed since the last run: run the checks again for a fresh answer.</p>}
      {!gate.neverRan && <MissingChecks canEdit={canEdit} gate={gate} onOpen={onOpen} onOpenSettings={onOpenSettings} />}
      {open && (
        <ul aria-label="Release gate rules" className="quality-gate-rules" id="quality-gate-rules">
          {rules.map((rule) => {
            const card = checks?.[rule.check];
            const shownState = ruleState(rule, blocked);
            return (
              <li className={`is-${rule.status}`} data-testid={`quality-gate-rule-${rule.id}`} key={rule.id}>
                <Pill className="is-xs quality-gate-state" tone={shownState.tone}>{shownState.label}</Pill>
                <div className="quality-grow">
                  <strong className="quality-gate-label">{rule.label}</strong>
                  <span className="quality-small quality-muted">{rule.status === 'fail' || rule.status === 'unknown' ? rule.detail : (rule.description || rule.detail)}</span>
                  {rule.id === 'readiness' && rule.status !== 'off' && <ReadinessItems canEdit={canEdit} onAskAI={onAskAI} rule={readiness} />}
                </div>
                {rule.status === 'fail' && canEdit && onAskAI && FIXABLE.includes(rule.check) && rule.id !== 'readiness' && (
                  <button className="quality-btn is-sm is-primary" onClick={() => onAskAI(gateRulePrompt(rule, card))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>
                )}
                {rule.status === 'fail' && rule.id === 'readiness' && canEdit && onAskAI && (
                  <button className="quality-btn is-sm is-primary" onClick={() => onAskAI(readinessPrompt((readiness?.items || []).filter((i) => i.blocks)))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>
                )}
                {rule.status !== 'off' && <button className="quality-btn is-sm" onClick={() => (rule.framework ? onOpen(rule.check, rule) : onOpen(rule.check))} type="button">Open</button>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

GateCard.propTypes = {
  canEdit: PropTypes.bool, checks: PropTypes.object, expandSignal: PropTypes.number, gate: PropTypes.object, onAskAI: PropTypes.func, onOpen: PropTypes.func.isRequired, onOpenSettings: PropTypes.func.isRequired,
};

function SbomDownload({ disabled, onDownload }) {
  const [format, setFormat] = useState('cyclonedx');
  return (
    <span className="quality-sbom">
      <label className="quality-sr" htmlFor="quality-sbom-format">SBOM format</label>
      <select className="quality-input is-sm" disabled={disabled} id="quality-sbom-format" onChange={(e) => setFormat(e.target.value)} value={format}>
        <option value="cyclonedx">CycloneDX</option>
        <option value="spdx">SPDX</option>
      </select>
      <button className="quality-btn is-sm" disabled={disabled} onClick={() => onDownload(format)} type="button"><DownloadSimple aria-hidden size={13} />Download</button>
    </span>
  );
}

SbomDownload.propTypes = { disabled: PropTypes.bool, onDownload: PropTypes.func.isRequired };

function CheckCard({ id, card, overview, service, canEdit, handlers, onRun, onOpen, onDownloadSbom, highlight, lockfile = null }) {
  const meta = CHECKS[id];
  const { running, history = [], settings } = overview;
  const live = running?.checks?.[id];
  const busy = live === 'running' || live === 'queued';
  const note = service ? (card.services || []).find((s) => s.service === service) : null;
  const pillStatus = busy ? live : (note ? note.status : shownStatus(card));
  const tone = statusOf(pillStatus).tone;
  const lines = service
    ? [...(note?.message ? [note.message] : []), ...(card.top || []).filter((f) => f.service === service).map((f) => findingLine(id, f))].slice(0, 3)
    : cardLines(id, card).filter((line) => line !== card.metric && line !== card.sub);
  const todo = busy ? null : whatToDo(id, card, { gate: overview.gate, checks: overview.checks });
  const points = sparkPoints(history, id);
  const action = cardAction(id, card);
  const when = whenText(settings?.when?.[id]);
  const attention = ['fail', 'warn'].includes(tone);
  // The run that was to do it stopped when the sandbox restarted (and it has not run since): never "Not run yet".
  const interrupted = !busy && interruptedCheck(overview, id);
  return (
    <article aria-labelledby={`quality-card-${id}`} className={`quality-card quality-check is-${tone}${attention ? ' is-attention' : ''}${highlight ? ' is-highlight' : ''}`} data-testid={`quality-card-${id}`}>
      <div className="quality-check-head">
        <CheckIcon path={meta.icon} />
        <h3 id={`quality-card-${id}`} title={CHECK_PURPOSE[id]}>{meta.title}</h3>
        {busy ? <Pill tone="prog"><span aria-hidden className="quality-spinner is-xs" />{live === 'queued' ? 'Queued' : 'Running'}</Pill> : <StatusPill status={pillStatus} />}
      </div>
      <div className="quality-check-metric">
        <div>
          {/* While it runs with no result yet, say so (2026-10-31: "Not run yet · Nothing to report" under a Running pill). */}
          <span className="quality-metric">{busy && !card.ranAt ? (live === 'queued' ? 'Queued' : 'Running…') : interrupted && !card.ranAt ? 'Interrupted' : card.metric || statusOf(card.status).label}</span>
          <span className="quality-muted quality-small">{busy && !card.ranAt ? 'The first result shows here when it ends.' : interrupted && !card.ranAt ? 'The sandbox restarted before it finished.' : card.sub || card.summary || ''}</span>
        </div>
        {points ? <Sparkline label={trendLabel(history, id)} points={points} tone={tone} /> : null}
      </div>
      {lines.length > 0 ? (
        <ul className="quality-lines">{lines.map((line, i) => <li key={i}>{line}</li>)}</ul>
      ) : ((!todo || service) && !(busy && !card.ranAt) && !(interrupted && !card.ranAt) && <p className="quality-muted quality-small">{service ? `Nothing for ${service}.` : 'Nothing to report.'}</p>)}
      {todo && (
        <div className={`quality-todo is-${tone}`} data-testid={`quality-todo-${id}`}>
          <span className="quality-todo-text"><strong>What to do: </strong>{todo.text}</span>
          {todo.action && (canEdit || todo.action.kind === 'open') && <ActionButton handlers={handlers} primary={attention || todo.action.kind !== 'open'} step={{ ...todo, check: id, origin: `overview:card:${id}` }} testId={`quality-todo-action-${id}`} />}
        </div>
      )}
      {lockfile?.at === `overview:card:${id}` && handlers.lockfileStatus(lockfile)}
      {handlers.runNoteAt(`overview:card:${id}`)}
      <div className="quality-check-foot">
        {interrupted ? (
          <span className="quality-small quality-grow quality-ink-warn" data-testid={`quality-interrupted-${id}`}>
            Interrupted — the sandbox restarted{card.ranAt ? ` · last result ${relativeTime(card.ranAt)}` : ''}
          </span>
        ) : (
          <span className="quality-muted quality-small quality-grow">
            {card.ranAt ? `${relativeTime(card.ranAt)} · ${when}` : `Not run yet · ${when}`}
            {card.ranAt && !points && history.length < 2 ? ' · first run' : ''}
          </span>
        )}
        {card.blocks && <Pill className="is-xs" tone="fail">Blocks Publish</Pill>}
        {canEdit && (
          <button aria-label={`${interrupted ? 'Retry' : 'Run'} ${meta.title}`} className={`quality-btn is-sm${interrupted ? ' is-primary' : ''}`} disabled={Boolean(running) || handlers.busy?.('runA11y')} onClick={() => onRun(id)} type="button">
            <Play aria-hidden size={12} weight="fill" />{interrupted ? 'Retry' : 'Run'}
          </button>
        )}
        {action === 'download' && <SbomDownload onDownload={onDownloadSbom} />}
        {action === 'fix' && canEdit && handlers.fix && todo?.action?.kind !== 'fix' && (
          <button className="quality-btn is-sm" onClick={() => handlers.fix({ check: id })} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>
        )}
        <button aria-label={`Open ${meta.title}`} className="quality-btn is-sm" onClick={() => onOpen(id)} type="button">Open</button>
      </div>
    </article>
  );
}

CheckCard.propTypes = {
  canEdit: PropTypes.bool,
  card: PropTypes.object.isRequired,
  handlers: PropTypes.object.isRequired,
  highlight: PropTypes.bool,
  id: PropTypes.string.isRequired,
  lockfile: PropTypes.object,
  onDownloadSbom: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  overview: PropTypes.object.isRequired,
  service: PropTypes.string,
};

/**
 * The last run stopped because the sandbox restarted (the overview's `interrupted`): what it was doing, and Retry for
 * its checks not run since (all of them when an older sandbox did not record which).
 */
function InterruptedRun({ interrupted, canEdit, busy, onRetry, note }) {
  const checks = interrupted.checks || [];
  const what = checks.length ? `${plural(checks.length, 'check')} did not finish: ${checks.map(checkTitle).join(', ')}.` : 'Its results were not saved.';
  return (
    <Banner
      actions={canEdit && onRetry ? (
        <button className="quality-btn is-sm is-primary" data-testid="quality-interrupted-retry" disabled={busy} onClick={() => onRetry(checks)} type="button">
          <Play aria-hidden size={12} weight="fill" />{checks.length ? 'Retry' : 'Run all checks'}
        </button>
      ) : null}
      role="status"
      testId="quality-interrupted"
      tone="warn"
    >
      <strong>Interrupted — the sandbox restarted{interrupted.current ? ` while running ${checkTitle(interrupted.current)}` : ''}.</strong>
      <span>{what}{interrupted.startedAt ? ` The run started ${relativeTime(interrupted.startedAt)}.` : ''}</span>
      {note}
    </Banner>
  );
}

InterruptedRun.propTypes = {
  busy: PropTypes.bool, canEdit: PropTypes.bool, interrupted: PropTypes.object.isRequired, note: PropTypes.node, onRetry: PropTypes.func,
};

/** "Last full run 2 min ago · 12 areas, by eddie" */
function lastRunText(run, serverAreas) {
  if (!run?.finishedAt) { return 'No run yet.'; }
  const areas = (run.checksRun || []).length;
  return `Last ${!run.checksRun || areas >= serverAreas ? 'full ' : ''}run ${relativeTime(run.finishedAt)}${areas ? ` · ${plural(areas, 'area')}` : ''}${run.by?.name ? `, by ${run.by.name}` : ''}${run.trigger === 'publish' ? ', before Publish' : ''}${run.state === 'interrupted' ? ' (interrupted)' : run.state === 'cancelled' ? ' (cancelled)' : ''}`;
}

export default function QualityOverview({
  overview, canEdit = false, onAskAI = null, onRunCheck, onRunAll = null, onOpenCheck, onOpenSettings, onDownloadSbom,
  onCreateLockfiles = null, onRunA11y = null, onPublish = null, lockfileBusy = false, a11yBusy = false, gateSignal = 0,
  lockfileStatus = null, onDismissLockfiles = () => {}, onStopLockfiles = null,
  onOpenProvenance = null, onOpenCompliance = null, onDownloadAttestation = null, onDownloadCompliance = null, onPrintCompliance = null,
  onOpenAssessments = null,
  assessmentList = null,
  onRetryInterrupted = null, runNote = null, onDismissRunNote = () => {},
}) {
  const [service, setService] = useState(null);
  const [filter, setFilter] = useState(null);
  const cardsRef = useRef(null);
  const checks = overview.checks || {};
  const services = (overview.detected?.services || []).map((s) => s.service);
  const counts = statusCounts(checks, overview.running);
  const areas = Object.keys(checks).filter((id) => CHECKS[id]).length;

  // Why the checks did not start, where it was clicked (QualityPanel `runNote`).
  const runNoteAt = (origin) => (runNote?.origin === origin ? <RunNote note={runNote} onDismiss={onDismissRunNote} /> : null);
  // Not memoised: the handlers read the latest overview on each render (cheap).
  const handlers = (() => ({
    runNoteAt,
    // A step's note whose step is gone (the list changed after the click): under the list.
    runNoteOrphan: (shown) => (runNote?.origin?.startsWith('overview:step:') && !shown.includes(runNote.origin) ? runNoteAt(runNote.origin) : null),
    fix: onAskAI ? (step) => {
      const card = checks[step.check];
      if (step.check === 'readiness') {
        const rule = (overview.gate?.rules || []).find((r) => r.id === 'readiness');
        const items = rule?.items?.length ? rule.items : (card?.top || []).map(readinessItemOf);
        onAskAI(readinessPrompt(items));
        return;
      }
      const rule = (overview.gate?.rules || []).find((r) => r.check === step.check && r.status === 'fail');
      onAskAI(rule ? gateRulePrompt(rule, card) : cardPrompt(step.check, card));
    } : null,
    lockfiles: onCreateLockfiles ? (step) => onCreateLockfiles(step?.origin || 'overview:card:vulnerabilities') : null,
    lockfileStatus: (state) => <LockfileStatus onDismiss={onDismissLockfiles} onStop={onStopLockfiles} state={state} />,
    writeTests: onAskAI ? () => onAskAI(writeTestsPrompt(services)) : null,
    runA11y: onRunA11y ? () => onRunA11y() : null,
    run: (step) => (step.check === 'accessibility' && onRunA11y ? onRunA11y() : onRunCheck(step.check, step.origin)),
    runAll: onRunAll ? (step) => onRunAll(step?.origin) : null,
    open: (step) => onOpenCheck(step.check),
    settings: () => onOpenSettings(),
    busy: (kind) => (kind === 'lockfiles' && lockfileBusy) || (kind === 'runA11y' && a11yBusy) || ((kind === 'runAll' || kind === 'run') && Boolean(overview.running)),
  }))();

  const pickFilter = (tone) => {
    const next = filter === tone ? null : tone;
    setFilter(next);
    if (next) { setTimeout(() => scrollInPanel(cardsRef.current), 0); }
  };
  const visible = (id) => matchesFilter(cardTone(id, checks[id], overview.running), filter);

  return (
    <div className="quality-overview" data-testid="quality-overview">
      <section aria-label="Overview" className="quality-top" id="quality-section-overview" tabIndex={-1}>
        <div className="quality-card quality-score">
          <ScoreRing score={overview.score} />
          <div className="quality-score-text">
            <h2 className="quality-title">{scoreTitle(overview.score, overview.gate)}</h2>
            <p>
              {plural(areas, 'area')} checked
              {services.length > 0 && <> across {services.map((s, i) => <React.Fragment key={s}>{i > 0 && (i === services.length - 1 ? ' and ' : ', ')}<span className="quality-mono">{s}</span></React.Fragment>)}</>}
              .
            </p>
            {counts.length > 0 && (
              <div aria-label="Filter the cards by status" className="quality-pills" data-testid="quality-status-counts" role="group">
                {counts.map((c) => (
                  <button aria-pressed={filter === c.tone} className={`quality-pill is-${c.tone} quality-pill-btn`} data-testid={`quality-chip-${c.tone}`} key={c.tone} onClick={() => pickFilter(c.tone)} type="button">{c.text}</button>
                ))}
              </div>
            )}
            <span className="quality-muted quality-small" data-testid="quality-last-run">{lastRunText(overview.run, areas - (overview.browserChecks || []).filter((id) => checks[id]).length)}</span>
            {overview.expiredIgnores > 0 && (
              <button className="quality-link is-warn" onClick={onOpenSettings} type="button">{plural(overview.expiredIgnores, 'ignore rule')} expired: review them</button>
            )}
          </div>
        </div>
        <NextSteps canEdit={canEdit} handlers={handlers} lockfile={lockfileStatus} onPublish={onPublish} overview={overview} />
      </section>

      {overview.interrupted && !overview.running && (
        <InterruptedRun busy={Boolean(overview.running)} canEdit={canEdit} interrupted={overview.interrupted} note={runNoteAt('overview:interrupted')} onRetry={onRetryInterrupted} />
      )}

      <GateCard canEdit={canEdit} checks={checks} expandSignal={gateSignal} gate={overview.gate} onAskAI={onAskAI} onOpen={onOpenCheck} onOpenSettings={onOpenSettings} />

      <div className="quality-filter-row" ref={cardsRef}>
        {services.length > 1 && (
          <div aria-label="Service" className="quality-chips" role="group">
            <span className="quality-cap">Service</span>
            <button aria-pressed={!service} className="quality-chip" onClick={() => setService(null)} type="button">All</button>
            {services.map((s) => (
              <button aria-pressed={service === s} className="quality-chip" key={s} onClick={() => setService(s)} type="button">{s}</button>
            ))}
          </div>
        )}
        {filter && (
          <p className="quality-filter-note" data-testid="quality-filter-note" role="status">
            Showing the cards {TONE_WORDS[filter]}. <button className="quality-link" onClick={() => setFilter(null)} type="button">Show all</button>
          </p>
        )}
      </div>

      {GROUPS.map((group) => {
        const ids = group.checks.filter((id) => CHECKS[id] && checks[id] && visible(id));
        // "Can I prove it?": the provenance and compliance cards, when the sandbox answers them (not under a status filter).
        const extras = group.id === 'prove' && !filter ? [
          overview.provenance && onOpenProvenance && (
            <ProvenanceCard key="provenance" onDownload={onDownloadAttestation || (() => {})} onOpen={() => onOpenProvenance()} onOpenItem={(id) => onOpenProvenance(id)} provenance={overview.provenance} />
          ),
          overview.compliance && onOpenCompliance && (
            <ComplianceCard compliance={overview.compliance} key="compliance" onDownloadJson={onDownloadCompliance || (() => {})} onOpen={onOpenCompliance} onPrint={onPrintCompliance || (() => {})} />
          ),
          overview.assessments && onOpenAssessments && (
            <AssessmentsCard all={assessmentList} assessments={overview.assessments} key="assessments" onOpen={onOpenAssessments} />
          ),
        ].filter(Boolean) : [];
        if (!ids.length && !extras.length) { return null; }
        return (
          <section aria-labelledby={`quality-group-${group.id}`} className="quality-group" id={`quality-section-${group.id}`} key={group.id} tabIndex={-1}>
            <div className="quality-group-head">
              <h2 id={`quality-group-${group.id}`}>{group.name}</h2>
              <span className="quality-muted">{group.desc}</span>
            </div>
            <div className="quality-cards" data-count={ids.length + extras.length}>
              {ids.map((id) => (
                <CheckCard
                  canEdit={canEdit}
                  card={checks[id]}
                  handlers={handlers}
                  highlight={Boolean(filter) && ['fail', 'warn'].includes(filter)}
                  id={id}
                  key={id}
                  lockfile={lockfileStatus}
                  onDownloadSbom={onDownloadSbom}
                  onOpen={onOpenCheck}
                  onRun={(check) => handlers.run({ check, origin: `overview:card:${check}` })}
                  overview={overview}
                  service={service}
                />
              ))}
              {extras}
            </div>
          </section>
        );
      })}
    </div>
  );
}

QualityOverview.propTypes = {
  a11yBusy: PropTypes.bool,
  canEdit: PropTypes.bool,
  gateSignal: PropTypes.number,
  lockfileBusy: PropTypes.bool,
  // Create lockfiles on this page (QualityPanel `lockfileAt`): `{ at, busy, job, note }`, `at` the step or card it shows in.
  lockfileStatus: PropTypes.object,
  onDismissLockfiles: PropTypes.func,
  onStopLockfiles: PropTypes.func,
  // (prompt) => void: the project chat, asked to fix it.
  onAskAI: PropTypes.func,
  // (origin) => void: Create lockfiles, from the step or card `origin` ('overview:step:<key>', 'overview:card:<id>').
  onCreateLockfiles: PropTypes.func,
  onDownloadSbom: PropTypes.func.isRequired,
  // (provenanceId) => void: the attestation of a release (.intoto.jsonl).
  onDownloadAttestation: PropTypes.func,
  // () => void: the compliance report as JSON.
  onDownloadCompliance: PropTypes.func,
  onOpenCheck: PropTypes.func.isRequired,
  // Every assessment, on or off (GET /quality/assessments `frameworks`): the Compliance assessments card's gauges.
  assessmentList: PropTypes.array,
  // (framework | null) => void: Quality / Compliance assessments, on one when given.
  onOpenAssessments: PropTypes.func,
  // () => void: Quality / Compliance mapping.
  onOpenCompliance: PropTypes.func,
  // (provenanceId?) => void: Quality / Build provenance, on a release when given.
  onOpenProvenance: PropTypes.func,
  // () => void: the printable compliance report in a new window.
  onPrintCompliance: PropTypes.func,
  onOpenSettings: PropTypes.func.isRequired,
  onPublish: PropTypes.func,
  onRunA11y: PropTypes.func,
  // (origin?) => void: Run all checks, from the step `origin` when it is one.
  onRunAll: PropTypes.func,
  // (checkId, origin?) => void: run one check, from the card or step `origin`.
  onRunCheck: PropTypes.func.isRequired,
  // (checks) => void: Retry the checks of the run the sandbox restart stopped (none named: every check).
  onRetryInterrupted: PropTypes.func,
  // Why the checks did not start: `{ origin, text }`, shown at the card, step or banner `origin`.
  runNote: PropTypes.object,
  onDismissRunNote: PropTypes.func,
  overview: PropTypes.object.isRequired,
};
